import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { alpha } from "@mui/material/styles";
import { STANDARD_TAGS_BY_GROUP } from "../../AssetSyndication/standardTags";
import {
  BRAND_BANNER_POSITIONS,
  BRAND_GRID_POSITIONS,
  BRAND_GROUPS,
  BrandEntry,
  BrandPlacement,
  blankBrand,
  countInvalidBrands,
  validateBrand,
} from "../brandOverlay";

interface Props {
  config: any;
  onChange: (field: string, value: any) => void;
}

const emptyPlacement = (): BrandPlacement => ({
  group: BRAND_GROUPS[0],
  image_tag: "",
  position: "bottom-right",
});

const BrandOverlayForm: React.FC<Props> = ({ config, onChange }) => {
  const theme = useTheme();
  const accent = theme.palette.status.accent;

  const brands: BrandEntry[] = Array.isArray(config.branding_images)
    ? config.branding_images
    : [];
  const incompleteCount = countInvalidBrands(brands);

  const writeBrands = (next: BrandEntry[]) => onChange("branding_images", next);

  const updateBrand = (index: number, patch: Partial<BrandEntry>) => {
    writeBrands(brands.map((brand, i) => (i === index ? { ...brand, ...patch } : brand)));
  };

  const updatePlacement = (
    brandIndex: number,
    placementIndex: number,
    patch: Partial<BrandPlacement>,
  ) => {
    const brand = brands[brandIndex];
    const placements = (brand?.placements || []).map((placement, i) =>
      i === placementIndex ? { ...placement, ...patch } : placement,
    );
    updateBrand(brandIndex, { placements });
  };

  const addPlacement = (brandIndex: number) => {
    const brand = brands[brandIndex];
    updateBrand(brandIndex, {
      placements: [...(brand?.placements || []), emptyPlacement()],
    });
  };

  const removePlacement = (brandIndex: number, placementIndex: number) => {
    const brand = brands[brandIndex];
    updateBrand(brandIndex, {
      placements: (brand?.placements || []).filter((_p, i) => i !== placementIndex),
    });
  };

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderLeft: `3px solid ${accent}`,
        borderRadius: 2,
        p: 2,
        mt: 1,
        boxShadow: "0 0 6px rgba(0,0,0,0.3)",
      }}
    >
      <Typography
        variant="subtitle1"
        sx={{ mb: 0.5, fontWeight: 600, color: accent, letterSpacing: 0.3 }}
      >
        Brand Overlay Configuration
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Each logo below is composited onto the images its placements name. Which
        images get branded at all is decided by the first syndicator's Image
        Ordering: a group is only branded when the first entry of its{" "}
        <code>&#123;group&#125;_GroupFallback</code> contains &quot;brand&quot;.
      </Typography>

      {brands.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-cy="brand-overlay-empty">
          No logos configured. Brand Overlay fails at validation with an empty
          list — add a logo or switch the step off.
        </Alert>
      )}

      {incompleteCount > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }} data-cy="brand-overlay-incomplete">
          {incompleteCount} {incompleteCount === 1 ? "logo has" : "logos have"} something the
          pipeline will reject. Assets fail this step until it&apos;s fixed.
        </Alert>
      )}

      <Stack spacing={2}>
        {brands.map((brand, brandIndex) => {
          const errors = validateBrand(brand);
          const placements = Array.isArray(brand?.placements) ? brand.placements : [];

          return (
            <Box
              key={brandIndex}
              sx={{
                border: `1px solid ${alpha(accent, 0.45)}`,
                borderRadius: 2,
                p: 2,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent }}>
                  Logo {brandIndex + 1}
                </Typography>
                <Tooltip title="Remove logo">
                  <IconButton
                    size="small"
                    aria-label={`Remove logo ${brandIndex + 1}`}
                    onClick={() => writeBrands(brands.filter((_b, i) => i !== brandIndex))}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Logo S3 Path"
                    placeholder="s3://bucket/path/logo.png"
                    fullWidth
                    size="small"
                    value={brand?.s3_path || ""}
                    onChange={(e) => updateBrand(brandIndex, { s3_path: e.target.value })}
                    error={Boolean(errors.s3_path)}
                    helperText={errors.s3_path || "PNG with transparency works best"}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Logo Size %"
                    type="number"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={brand?.logo_pct ?? ""}
                    onChange={(e) => updateBrand(brandIndex, { logo_pct: e.target.value })}
                    error={Boolean(errors.logo_pct)}
                    helperText={errors.logo_pct || "% of the placement cell"}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Padding %"
                    type="number"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={brand?.padding_pct ?? ""}
                    onChange={(e) => updateBrand(brandIndex, { padding_pct: e.target.value })}
                    error={Boolean(errors.padding_pct)}
                    helperText={errors.padding_pct || "Per side, inside the cell"}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={brand?.alt_color_allowed ?? false}
                        onChange={(e) =>
                          updateBrand(brandIndex, { alt_color_allowed: e.target.checked })
                        }
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500 }}>Recolour on bright</Typography>
                    }
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Alt Colour"
                    placeholder="#FFFFFF"
                    fullWidth
                    size="small"
                    disabled={!brand?.alt_color_allowed}
                    value={brand?.alt_color_value || ""}
                    onChange={(e) =>
                      updateBrand(brandIndex, { alt_color_value: e.target.value })
                    }
                    error={Boolean(errors.alt_color_value)}
                    helperText={errors.alt_color_value || "Used on bright backgrounds"}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: accent }}>
                  Placements
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addPlacement(brandIndex)}
                  sx={{ textTransform: "none", color: accent }}
                >
                  Add placement
                </Button>
              </Stack>

              {errors.placements && (
                <Typography variant="caption" sx={{ color: "warning.main" }}>
                  {errors.placements}
                </Typography>
              )}

              <Stack spacing={1.5} sx={{ mt: 1 }}>
                {placements.map((placement, placementIndex) => {
                  const rowErrors = errors.placementErrors[placementIndex] || {};
                  const tagSuggestions = STANDARD_TAGS_BY_GROUP[placement?.group] || [];

                  return (
                    <Grid container spacing={1} alignItems="flex-start" key={placementIndex}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          select
                          label="Group"
                          fullWidth
                          size="small"
                          value={placement?.group || ""}
                          onChange={(e) =>
                            updatePlacement(brandIndex, placementIndex, {
                              group: e.target.value,
                            })
                          }
                          error={Boolean(rowErrors.group)}
                          helperText={rowErrors.group}
                        >
                          {BRAND_GROUPS.map((group) => (
                            <MenuItem key={group} value={group}>
                              {group}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          freeSolo
                          size="small"
                          forcePopupIcon
                          openOnFocus
                          selectOnFocus
                          handleHomeEndKeys
                          clearOnBlur={false}
                          options={tagSuggestions}
                          value={placement?.image_tag || ""}
                          onInputChange={(_e, next) => {
                            // Autocomplete also fires this on mount ("reset"),
                            // which would mark the config dirty before anyone
                            // touched it.
                            if ((next || "") === (placement?.image_tag || "")) return;
                            updatePlacement(brandIndex, placementIndex, {
                              image_tag: next || "",
                            });
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Image Tag"
                              placeholder="Pick or type custom"
                              error={Boolean(rowErrors.image_tag)}
                              helperText={rowErrors.image_tag}
                            />
                          )}
                        />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          select
                          label="Position"
                          fullWidth
                          size="small"
                          value={placement?.position || ""}
                          onChange={(e) =>
                            updatePlacement(brandIndex, placementIndex, {
                              position: e.target.value,
                            })
                          }
                          error={Boolean(rowErrors.position)}
                          helperText={rowErrors.position}
                        >
                          {BRAND_GRID_POSITIONS.map((position) => (
                            <MenuItem key={position} value={position}>
                              {position}
                            </MenuItem>
                          ))}
                          {BRAND_BANNER_POSITIONS.map((position) => (
                            <MenuItem key={position} value={position}>
                              {position} (not rendered yet)
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>

                      <Grid item xs={12} sm={1}>
                        <IconButton
                          size="small"
                          aria-label={`Remove placement ${placementIndex + 1}`}
                          onClick={() => removePlacement(brandIndex, placementIndex)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  );
                })}
              </Stack>
            </Box>
          );
        })}
      </Stack>

      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={() => writeBrands([...brands, blankBrand()])}
        sx={{ mt: 2, textTransform: "none", color: accent }}
      >
        Add logo
      </Button>
    </Box>
  );
};

export default BrandOverlayForm;
